import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Alert from 'flarum/common/components/Alert';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import Icon from 'flarum/common/components/Icon';
import { usingAppleWebview, requestPushPermissions, usePWABuilder } from './use-pwa-builder';
const subscribeUser = async save => {
  var _app$sw;
  if (!((_app$sw = app.sw) != null && _app$sw.pushManager)) return;
  const subscription = await app.sw.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: app.forum.attribute('vapidPublicKey')
  });
  if (!save) return;
  await app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/pwa/push',
    body: {
      subscription
    }
  });
};
const pushEnabled = () => {
  if (!app.session.user) return false;
  const preferences = app.session.user.preferences();
  for (const key in preferences) {
    if (typeof key === 'string' && key.startsWith('notify_') && key.endsWith('_push') && preferences[key]) {
      return true;
    }
  }
  return false;
};
const supportsBrowserNotifications = () => 'Notification' in window;
export const refreshSubscription = async sw => {
  if (app.cache.pwaRefreshed || !supportsBrowserNotifications() || window.Notification.permission !== 'granted' || !pushEnabled()) {
    app.cache.pwaRefreshed = true;
    return;
  }
  try {
    await subscribeUser(true);
  } catch (e) {
    if (!sw.pushManager) {
      return;
    }
    const subscription = await sw.pushManager.getSubscription();
    if (subscription) {
      await subscription.unsubscribe();
      await subscribeUser(true);
    }
  }
  app.cache.pwaRefreshed = true;
};
const pushConfigured = () => {
  return !!app.forum.attribute('vapidPublicKey');
};
const _usePWABuilder = usePWABuilder(),
  registerFirebasePushNotificationListeners = _usePWABuilder.registerFirebasePushNotificationListeners,
  removeFirebasePushNotificationListeners = _usePWABuilder.removeFirebasePushNotificationListeners,
  hasFirebasePushState = _usePWABuilder.hasFirebasePushState;
export default function addPushNotifications() {
  extend('flarum/common/components/Page', 'oncreate', () => {
    if (!pushConfigured()) return;
    const dismissAlert = () => {
      localStorage.setItem('askvortsov-pwa.notif-alert.dismissed', JSON.stringify({
        timestamp: new Date().getTime()
      }));
    };
    app.alerts.dismiss(app.cache.pwaNotifsAlert);
    if (!localStorage.getItem('askvortsov-pwa.notif-alert.dismissed') && supportsBrowserNotifications() && window.Notification.permission === 'default' && pushEnabled()) {
      app.cache.pwaNotifsAlert = app.alerts.show({
        controls: [m(Link, {
          className: "Button Button--link",
          href: app.route('settings'),
          onclick: () => dismissAlert()
        }, app.translator.trans('fof-pwa.forum.alerts.optin_button'))],
        ondismiss: dismissAlert
      }, app.translator.trans('fof-pwa.forum.alerts.optin'));
    }
  });
  extend('flarum/forum/components/NotificationGrid', 'notificationMethods', function (items) {
    if (!pushConfigured()) return;
    items.add('push', {
      name: 'push',
      icon: 'fas fa-mobile',
      label: app.translator.trans('fof-pwa.forum.settings.push_header')
    });
  });
  extend('flarum/forum/components/SettingsPage', 'notificationsItems', function (items) {
    if (usingAppleWebview() || !pushConfigured()) return;
    if (!supportsBrowserNotifications()) {
      items.add('push-no-browser-support', m(Alert, {
        dismissible: false,
        controls: [m("a", {
          className: "Button Button--link",
          href: "https://developer.mozilla.org/en-US/docs/Web/API/Push_API",
          target: "_blank",
          rel: "noopener noreferrer"
        }, app.translator.trans('fof-pwa.forum.settings.pwa_notifications.no_browser_support_button'))]
      }, m(Icon, {
        name: "fas fa-exclamation-triangle"
      }), app.translator.trans('fof-pwa.forum.settings.pwa_notifications.no_browser_support')), 10);
      return;
    }
    if (window.Notification.permission === 'default') {
      items.add('push-optin-default', m(Alert, {
        dismissible: false,
        attrs: {
          className: 'pwa-setting-alert'
        },
        controls: [m(Button, {
          className: "Button Button--link",
          onclick: () => {
            const requestPermission = window.Notification.requestPermission();
            if (requestPermission instanceof Promise) {
              requestPermission.then(res => {
                m.redraw();
                if (res === 'granted') {
                  subscribeUser(true);
                }
              });
            } else {
              // Legacy callback API
              window.Notification.requestPermission(res => {
                m.redraw();
                if (res === 'granted') {
                  subscribeUser(true);
                }
              });
            }
          }
        }, app.translator.trans('fof-pwa.forum.settings.pwa_notifications.access_default_button'))]
      }, m(Icon, {
        name: "fas fa-exclamation-circle"
      }), app.translator.trans('fof-pwa.forum.settings.pwa_notifications.access_default')), 10);
    } else if (window.Notification.permission === 'denied') {
      items.add('push-optin-denied', m(Alert, {
        type: "error",
        dismissible: false,
        attrs: {
          className: 'pwa-setting-alert'
        },
        controls: [m("a", {
          className: "Button Button--link",
          href: "https://support.humblebundle.com/hc/en-US/articles/360008513933-Enabling-and-Disabling-Browser-Notifications-in-Various-Browsers",
          target: "_blank",
          rel: "noopener noreferrer"
        }, app.translator.trans('fof-pwa.forum.settings.pwa_notifications.access_denied_button'))]
      }, m(Icon, {
        name: "fas fa-exclamation-triangle"
      }), app.translator.trans('fof-pwa.forum.settings.pwa_notifications.access_denied')), 10);
    }
  });
  extend('flarum/forum/components/SettingsPage', 'notificationsItems', function (items) {
    if (!usingAppleWebview()) return;
    if (!hasFirebasePushState('authorized')) {
      items.add('firebase-push-optin-default', m(Alert, {
        dismissible: false,
        attrs: {
          className: 'pwa-setting-alert'
        },
        controls: [m(Button, {
          className: "Button Button--link",
          onclick: () => requestPushPermissions()
        }, app.translator.trans('fof-pwa.forum.settings.pwa_notifications.access_default_button'))]
      }, m(Icon, {
        name: "fas fa-exclamation-circle"
      }), app.translator.trans('fof-pwa.forum.settings.pwa_notifications.access_default')), 10);
    }
  });
  extend('flarum/forum/components/SettingsPage', 'oncreate', function () {
    registerFirebasePushNotificationListeners();
  });
  extend('flarum/forum/components/SettingsPage', 'onremove', function () {
    removeFirebasePushNotificationListeners();
  });
}